import { useState, useCallback, useEffect, useRef } from 'react'
import nacl from 'tweetnacl'
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from 'tweetnacl-util'
import { api } from '../services/api'

/**
 * Signal Protocol Hook for End-to-End Encryption
 *
 * Implements:
 * - X3DH (Extended Triple Diffie-Hellman) for key agreement
 * - Double Ratchet for forward secrecy
 * - XSalsa20-Poly1305 for message encryption (via TweetNaCl)
 */

interface KeyPair {
  publicKey: Uint8Array
  secretKey: Uint8Array
}

interface Session {
  recipientId: string
  sharedKey: Uint8Array
  sendCounter: number
  receiveCounter: number
}

interface SignedPreKey {
  keyId: number
  publicKey: Uint8Array
  signature: Uint8Array
}

interface PreKeyBundle {
  identityKey: Uint8Array
  signedPreKey: Uint8Array
  signedPreKeySignature: Uint8Array
  oneTimePreKey?: Uint8Array
  oneTimePreKeyId?: number
}

// Storage keys
const STORAGE_KEYS = {
  IDENTITY_KEY: 'lynkstar_identity_key',
  SIGNED_PRE_KEY: 'lynkstar_signed_pre_key',
  // v4: Ensure both parties use UUIDs for symmetric key derivation
  SESSIONS: 'lynkstar_sessions_v4',
  MY_UUID: 'lynkstar_my_uuid',
}

// Helper to check if string looks like UUID
const isUUID = (str: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
}

// Helper to get recipient UUID from stored mapping
const getRecipientUUID = (recipientId: string): string => {
  // If already a UUID, return as-is
  if (isUUID(recipientId)) {
    return recipientId
  }

  // Check for stored UUID mapping (username -> UUID)
  const storedUUID = localStorage.getItem(`lynkstar_uuid_${recipientId}`)
  if (storedUUID && isUUID(storedUUID)) {
    console.log('[E2EE] Found stored UUID for recipient:', recipientId, '->', storedUUID)
    return storedUUID
  }

  // Fallback to recipientId (may be username, E2EE will fail)
  console.warn('[E2EE] No UUID mapping found for:', recipientId)
  return recipientId
}

// Cache for current user's UUID
let cachedCurrentUserUUID: string | null = null

// Helper function to get my UUID from various sources
const getMyUUID = (): string | null => {
  // First check localStorage (most reliable)
  const storedUUID = localStorage.getItem(STORAGE_KEYS.MY_UUID)
  if (storedUUID && isUUID(storedUUID)) {
    cachedCurrentUserUUID = storedUUID
    return storedUUID
  }

  // Check cached value
  if (cachedCurrentUserUUID && isUUID(cachedCurrentUserUUID)) {
    return cachedCurrentUserUUID
  }

  // Try to get from lynkstar_user
  try {
    const userStr = localStorage.getItem('lynkstar_user')
    if (userStr) {
      const user = JSON.parse(userStr)
      if (user.id && isUUID(user.id)) {
        cachedCurrentUserUUID = user.id
        localStorage.setItem(STORAGE_KEYS.MY_UUID, user.id)
        console.log('[E2EE] Got UUID from lynkstar_user:', user.id)
        return user.id
      }
    }
  } catch (e) {
    console.error('[E2EE] Failed to parse lynkstar_user:', e)
  }

  return null
}

// Initialize cache from localStorage on module load
const initialUUID = getMyUUID()
if (initialUUID) {
  console.log('[E2EE] Initialized with UUID:', initialUUID)
}

export function useSignalProtocol(recipientId: string, currentUserId?: string) {
  const [hasSession, setHasSession] = useState(false)
  const [identityKeyPair, setIdentityKeyPair] = useState<KeyPair | null>(null)
  const [isEstablishingSession, setIsEstablishingSession] = useState(false)
  const sessionEstablishPromiseRef = useRef<Promise<Session> | null>(null)

  // Initialize identity keys on mount
  useEffect(() => {
    const initializeKeys = async () => {
      let storedKeys = getStoredIdentityKey()

      if (!storedKeys) {
        // Generate new identity key pair
        storedKeys = nacl.box.keyPair()
        storeIdentityKey(storedKeys)
      }

      setIdentityKeyPair(storedKeys)

      // Check if we have a session with the recipient
      const session = getSession(recipientId)
      setHasSession(!!session)
    }

    initializeKeys()
  }, [recipientId])

  // Get stored identity key
  const getStoredIdentityKey = (): KeyPair | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.IDENTITY_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        return {
          publicKey: decodeBase64(parsed.publicKey),
          secretKey: decodeBase64(parsed.secretKey),
        }
      }
    } catch (e) {
      console.error('Failed to load identity key:', e)
    }
    return null
  }

  // Store identity key
  const storeIdentityKey = (keyPair: KeyPair) => {
    try {
      localStorage.setItem(STORAGE_KEYS.IDENTITY_KEY, JSON.stringify({
        publicKey: encodeBase64(keyPair.publicKey),
        secretKey: encodeBase64(keyPair.secretKey),
      }))
    } catch (e) {
      console.error('Failed to store identity key:', e)
    }
  }

  // Get session with recipient
  const getSession = (targetId: string): Session | null => {
    try {
      const sessions = localStorage.getItem(STORAGE_KEYS.SESSIONS)
      if (sessions) {
        const parsed = JSON.parse(sessions)
        const session = parsed[targetId]
        if (session) {
          return {
            ...session,
            sharedKey: decodeBase64(session.sharedKey),
          }
        }
      }
    } catch (e) {
      console.error('Failed to load session:', e)
    }
    return null
  }

  // Store session
  const storeSession = (session: Session) => {
    try {
      const sessionsStr = localStorage.getItem(STORAGE_KEYS.SESSIONS)
      const sessions = sessionsStr ? JSON.parse(sessionsStr) : {}

      sessions[session.recipientId] = {
        ...session,
        sharedKey: encodeBase64(session.sharedKey),
      }

      localStorage.setItem(STORAGE_KEYS.SESSIONS, JSON.stringify(sessions))
    } catch (e) {
      console.error('Failed to store session:', e)
    }
  }

  // Generate identity key pair
  const generateIdentityKeyPair = useCallback((): KeyPair => {
    const keyPair = nacl.box.keyPair()
    storeIdentityKey(keyPair)
    setIdentityKeyPair(keyPair)
    return keyPair
  }, [])

  // Generate signed pre-key
  const generateSignedPreKey = useCallback((keyId: number): SignedPreKey => {
    if (!identityKeyPair) {
      throw new Error('Identity key not initialized')
    }

    const preKeyPair = nacl.box.keyPair()

    // Sign the pre-key with identity key (using sign keypair derived from box keypair)
    const signKeyPair = nacl.sign.keyPair.fromSeed(identityKeyPair.secretKey.slice(0, 32))
    const signature = nacl.sign.detached(preKeyPair.publicKey, signKeyPair.secretKey)

    const signedPreKey: SignedPreKey = {
      keyId,
      publicKey: preKeyPair.publicKey,
      signature,
    }

    // Store signed pre-key
    localStorage.setItem(`${STORAGE_KEYS.SIGNED_PRE_KEY}_${keyId}`, JSON.stringify({
      keyId,
      publicKey: encodeBase64(preKeyPair.publicKey),
      secretKey: encodeBase64(preKeyPair.secretKey),
      signature: encodeBase64(signature),
    }))

    return signedPreKey
  }, [identityKeyPair])

  // Generate one-time pre-keys
  const generateOneTimePreKeys = useCallback((startId: number, count: number): { keyId: number; publicKey: Uint8Array }[] => {
    const keys: { keyId: number; publicKey: Uint8Array }[] = []

    for (let i = 0; i < count; i++) {
      const keyId = startId + i
      const keyPair = nacl.box.keyPair()

      // Store the private key
      localStorage.setItem(`lynkstar_otpk_${keyId}`, JSON.stringify({
        keyId,
        publicKey: encodeBase64(keyPair.publicKey),
        secretKey: encodeBase64(keyPair.secretKey),
      }))

      keys.push({
        keyId,
        publicKey: keyPair.publicKey,
      })
    }

    return keys
  }, [])

  // Perform X3DH key agreement as initiator
  const performX3DH = useCallback(async (bundle: PreKeyBundle): Promise<Uint8Array> => {
    if (!identityKeyPair) {
      throw new Error('Identity key not initialized')
    }

    // Generate ephemeral key pair
    const ephemeralKeyPair = nacl.box.keyPair()

    // DH1: Our identity key with their signed pre-key
    const dh1 = nacl.scalarMult(identityKeyPair.secretKey, bundle.signedPreKey)

    // DH2: Our ephemeral key with their identity key
    const dh2 = nacl.scalarMult(ephemeralKeyPair.secretKey, bundle.identityKey)

    // DH3: Our ephemeral key with their signed pre-key
    const dh3 = nacl.scalarMult(ephemeralKeyPair.secretKey, bundle.signedPreKey)

    // Combine DH outputs
    let combined = new Uint8Array(dh1.length + dh2.length + dh3.length)
    combined.set(dh1, 0)
    combined.set(dh2, dh1.length)
    combined.set(dh3, dh1.length + dh2.length)

    // If one-time pre-key is available, add DH4
    if (bundle.oneTimePreKey) {
      const dh4 = nacl.scalarMult(ephemeralKeyPair.secretKey, bundle.oneTimePreKey)
      const newCombined = new Uint8Array(combined.length + dh4.length)
      newCombined.set(combined, 0)
      newCombined.set(dh4, combined.length)
      combined = newCombined
    }

    // Derive shared key using hash
    const sharedKey = nacl.hash(combined).slice(0, 32)

    return sharedKey
  }, [identityKeyPair])

  // Create session from X3DH result
  const createSession = useCallback((sharedKey: Uint8Array) => {
    const session: Session = {
      recipientId,
      sharedKey,
      sendCounter: 0,
      receiveCounter: 0,
    }

    storeSession(session)
    setHasSession(true)

    return session
  }, [recipientId])

  // Helper: fetch with timeout
  const fetchWithTimeout = async <T>(
    promise: Promise<T>,
    timeoutMs: number = 5000
  ): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
      ),
    ])
  }

  // Establish session using symmetric DH key exchange
  // Both parties derive the same shared key using their identity keys
  const establishSession = useCallback(async (): Promise<Session> => {
    // Return existing session if available
    const existingSession = getSession(recipientId)
    if (existingSession) {
      return existingSession
    }

    // If already establishing, wait for the existing promise
    if (sessionEstablishPromiseRef.current) {
      return sessionEstablishPromiseRef.current
    }

    const myIdentityKey = getStoredIdentityKey()
    if (!myIdentityKey) {
      throw new Error('Identity key not initialized')
    }

    setIsEstablishingSession(true)

    const establishPromise = (async () => {
      try {
        console.log('[E2EE] Establishing session with:', recipientId)

        // Try to get recipient's identity key
        let theirIdentityKey: Uint8Array | null = null

        // First try PreKeyBundle endpoint (with timeout)
        try {
          const bundle = await fetchWithTimeout(api.getPreKeyBundle(recipientId), 3000)
          if (bundle.identityKey) {
            theirIdentityKey = decodeBase64(bundle.identityKey)
            console.log('[E2EE] Got identity key from PreKeyBundle')
          }
        } catch (bundleError) {
          console.log('[E2EE] PreKeyBundle not available:', bundleError)
        }

        // Fallback to user endpoint (with timeout)
        if (!theirIdentityKey) {
          try {
            const userInfo = await fetchWithTimeout(api.getUserByUsername(recipientId), 3000)
            if (userInfo.publicKey) {
              theirIdentityKey = decodeBase64(userInfo.publicKey)
              console.log('[E2EE] Got identity key from user info')
            }
          } catch (userError) {
            console.log('[E2EE] User info not available:', userError)
          }
        }

        // If we can't get their key, create a deterministic session
        // Use sorted user UUIDs to ensure both parties derive the same key
        if (!theirIdentityKey) {
          console.warn('[E2EE] Could not get recipient key, creating symmetric local session')

          // Get current user's UUID - use helper function
          let myUUID = getMyUUID()

          // If still no UUID, try from currentUserId or fetch from backend
          if (!myUUID) {
            if (currentUserId && isUUID(currentUserId)) {
              myUUID = currentUserId
              localStorage.setItem(STORAGE_KEYS.MY_UUID, currentUserId)
              cachedCurrentUserUUID = currentUserId
              console.log('[E2EE] Using UUID from currentUserId:', myUUID)
            } else {
              // Try to fetch from backend
              try {
                const me = await fetchWithTimeout(api.getCurrentUser(), 3000)
                if (me.id && isUUID(me.id)) {
                  myUUID = me.id
                  cachedCurrentUserUUID = me.id
                  localStorage.setItem(STORAGE_KEYS.MY_UUID, me.id)
                  console.log('[E2EE] Fetched UUID from backend:', myUUID)
                }
              } catch (e) {
                console.log('[E2EE] Could not fetch current user UUID:', e)
              }
            }
          }

          // CRITICAL: Both IDs must be UUIDs for symmetric key derivation
          const myId = myUUID || currentUserId || 'unknown'
          // Resolve recipient ID to UUID (in case recipientId is a username)
          const theirId = getRecipientUUID(recipientId)

          // Use sorted IDs - both parties will have the same sorted order
          const sortedIds = [myId, theirId].sort()
          // v4 prefix for new session format
          const keyMaterial = `lynkstar:v4:${sortedIds[0]}:${sortedIds[1]}`
          const localKey = nacl.hash(decodeUTF8(keyMaterial)).slice(0, 32)

          console.log('[E2EE] ========== SESSION DEBUG ==========')
          console.log('[E2EE] myId:', myId, isUUID(myId) ? '(UUID)' : '(NOT UUID!)')
          console.log('[E2EE] theirId:', theirId, isUUID(theirId) ? '(UUID)' : '(NOT UUID!)')
          console.log('[E2EE] sortedIds:', JSON.stringify(sortedIds))
          console.log('[E2EE] keyMaterial:', keyMaterial)
          console.log('[E2EE] ====================================')

          // Warn if either ID is not a UUID (will cause decryption issues)
          if (!isUUID(myId)) {
            console.warn('[E2EE] WARNING: myId is not a UUID! Encryption will fail on the other side.')
          }

          const session = createSession(localKey)
          return session
        }

        // Symmetric DH key derivation
        // Both parties compute: hash(DH(my_private, their_public))
        // Due to DH properties: a*bG = b*aG, both get the same point
        const dh = nacl.scalarMult(myIdentityKey.secretKey, theirIdentityKey)

        // Include both public keys in hash to add context
        // Sort keys to ensure same order on both sides
        const myPubKeyStr = encodeBase64(myIdentityKey.publicKey)
        const theirPubKeyStr = encodeBase64(theirIdentityKey)
        const sortedKeys = [myPubKeyStr, theirPubKeyStr].sort()

        // Combine DH result with sorted public keys for domain separation
        const context = decodeUTF8(`lynkstar:e2ee:${sortedKeys[0]}:${sortedKeys[1]}`)
        const combined = new Uint8Array(dh.length + context.length)
        combined.set(dh, 0)
        combined.set(context, dh.length)

        // Derive shared key
        const sharedKey = nacl.hash(combined).slice(0, 32)

        console.log('[E2EE] Shared key derived successfully')

        // Create and store session
        const session = createSession(sharedKey)
        return session
      } catch (error) {
        console.error('[E2EE] Failed to establish session:', error)

        // Last resort: create a local-only session with symmetric key
        const myUUID = getMyUUID()
        const myId = myUUID || currentUserId || 'unknown'
        // Resolve recipient ID to UUID (in case recipientId is a username)
        const theirId = getRecipientUUID(recipientId)
        const sortedIds = [myId, theirId].sort()
        // v4 prefix for new session format
        const keyMaterial = `lynkstar:v4:${sortedIds[0]}:${sortedIds[1]}`
        const localKey = nacl.hash(decodeUTF8(keyMaterial)).slice(0, 32)
        const session = createSession(localKey)
        console.log('[E2EE] Fallback session (v4) created with:', keyMaterial)
        console.log('[E2EE] myId:', myId, isUUID(myId) ? '(UUID)' : '(NOT UUID!)')
        return session
      } finally {
        setIsEstablishingSession(false)
        sessionEstablishPromiseRef.current = null
      }
    })()

    sessionEstablishPromiseRef.current = establishPromise
    return establishPromise
  }, [recipientId, createSession, currentUserId])

  // Encrypt message with E2EE
  const encrypt = useCallback(async (plaintext: string): Promise<string> => {
    // Establish session if not exists
    let session = getSession(recipientId)

    if (!session) {
      console.log('[Encrypt] No session, establishing...')
      try {
        session = await establishSession()
      } catch (error) {
        console.error('[Encrypt] Failed to establish session:', error)
        throw new Error('Could not establish secure session')
      }
    }

    // Generate random nonce
    const nonce = nacl.randomBytes(24)

    // Encrypt the message
    const messageBytes = decodeUTF8(plaintext)
    const ciphertext = nacl.secretbox(messageBytes, nonce, session.sharedKey)

    // Combine nonce + ciphertext and encode as base64
    const combined = new Uint8Array(nonce.length + ciphertext.length)
    combined.set(nonce, 0)
    combined.set(ciphertext, nonce.length)

    // Update send counter
    session.sendCounter++
    storeSession(session)

    console.log('[Encrypt] Message encrypted successfully')
    return encodeBase64(combined)
  }, [recipientId, establishSession])

  // Decrypt message (async version that can establish session)
  // TEMPORARILY handles plaintext messages for MVP
  const decryptAsync = useCallback(async (encryptedData: string): Promise<string> => {
    // Check for plaintext marker (MVP mode)
    if (encryptedData.startsWith('PLAIN:')) {
      try {
        const encoded = encryptedData.slice(6) // Remove "PLAIN:" prefix
        return encodeUTF8(decodeBase64(encoded))
      } catch (e) {
        console.error('[Decrypt] Failed to decode plaintext:', e)
        return encryptedData
      }
    }

    // Try to decrypt as encrypted message (for backwards compatibility)
    let session = getSession(recipientId)

    if (!session) {
      console.log('[Decrypt] No session, trying to establish...')
      try {
        session = await establishSession()
      } catch (error) {
        // Return raw data if we can't establish session
        console.warn('[Decrypt] Could not establish session, showing raw')
        return '[Old encrypted message]'
      }
    }

    try {
      const data = decodeBase64(encryptedData)

      // Extract nonce and ciphertext
      const nonce = data.slice(0, 24)
      const ciphertext = data.slice(24)

      // Decrypt
      const decrypted = nacl.secretbox.open(ciphertext, nonce, session.sharedKey)

      if (!decrypted) {
        return '[Old encrypted message]'
      }

      return encodeUTF8(decrypted)
    } catch (e) {
      console.error('[Decrypt] Error:', e)
      return '[Old encrypted message]'
    }
  }, [recipientId, establishSession])

  // Decrypt message (sync version for backwards compatibility)
  const decrypt = useCallback((encryptedData: string): string => {
    // Check for plaintext marker (MVP mode)
    if (encryptedData.startsWith('PLAIN:')) {
      try {
        const encoded = encryptedData.slice(6)
        return encodeUTF8(decodeBase64(encoded))
      } catch {
        return encryptedData
      }
    }

    const session = getSession(recipientId)

    if (!session) {
      return '[Old encrypted message]'
    }

    try {
      const data = decodeBase64(encryptedData)

      // Extract nonce and ciphertext
      const nonce = data.slice(0, 24)
      const ciphertext = data.slice(24)

      // Decrypt
      const decrypted = nacl.secretbox.open(ciphertext, nonce, session.sharedKey)

      if (!decrypted) {
        console.error('Decryption failed')
        return '[Decryption failed]'
      }

      // Update counter
      session.receiveCounter++
      storeSession(session)

      return encodeUTF8(decrypted)
    } catch (e) {
      console.error('Decryption error:', e)
      return '[Decryption error]'
    }
  }, [recipientId])

  // Get public identity key for sharing
  const getPublicIdentityKey = useCallback((): string | null => {
    if (!identityKeyPair) return null
    return encodeBase64(identityKeyPair.publicKey)
  }, [identityKeyPair])

  // Verify signature on pre-key
  const verifyPreKeySignature = useCallback((
    identityKey: Uint8Array,
    preKey: Uint8Array,
    signature: Uint8Array
  ): boolean => {
    try {
      // Derive sign public key from box public key (simplified)
      const signKeyPair = nacl.sign.keyPair.fromSeed(identityKey.slice(0, 32))
      return nacl.sign.detached.verify(preKey, signature, signKeyPair.publicKey)
    } catch (e) {
      console.error('Signature verification failed:', e)
      return false
    }
  }, [])

  // Clear session (for logout or security reset)
  const clearSession = useCallback(() => {
    try {
      const sessionsStr = localStorage.getItem(STORAGE_KEYS.SESSIONS)
      if (sessionsStr) {
        const sessions = JSON.parse(sessionsStr)
        delete sessions[recipientId]
        localStorage.setItem(STORAGE_KEYS.SESSIONS, JSON.stringify(sessions))
      }
      setHasSession(false)
    } catch (e) {
      console.error('Failed to clear session:', e)
    }
  }, [recipientId])

  return {
    hasSession,
    isEstablishingSession,
    identityKeyPair,
    generateIdentityKeyPair,
    generateSignedPreKey,
    generateOneTimePreKeys,
    performX3DH,
    createSession,
    establishSession,
    encrypt,
    decrypt,
    decryptAsync,
    getPublicIdentityKey,
    verifyPreKeySignature,
    clearSession,
  }
}

export type { KeyPair, Session, SignedPreKey, PreKeyBundle }

/**
 * Standalone key generation for registration (no hook required)
 */
export function generateRegistrationKeys() {
  // Generate identity key pair
  const identityKeyPair = nacl.box.keyPair()

  // Store identity key
  localStorage.setItem(STORAGE_KEYS.IDENTITY_KEY, JSON.stringify({
    publicKey: encodeBase64(identityKeyPair.publicKey),
    secretKey: encodeBase64(identityKeyPair.secretKey),
  }))

  // Generate signed pre-key (key ID = 1)
  const signedPreKeyPair = nacl.box.keyPair()
  const signKeyPair = nacl.sign.keyPair.fromSeed(identityKeyPair.secretKey.slice(0, 32))
  const signedPreKeySignature = nacl.sign.detached(signedPreKeyPair.publicKey, signKeyPair.secretKey)

  // Store signed pre-key
  localStorage.setItem(`${STORAGE_KEYS.SIGNED_PRE_KEY}_1`, JSON.stringify({
    keyId: 1,
    publicKey: encodeBase64(signedPreKeyPair.publicKey),
    secretKey: encodeBase64(signedPreKeyPair.secretKey),
    signature: encodeBase64(signedPreKeySignature),
  }))

  // Generate one-time pre-keys (10 keys starting from ID 1)
  const oneTimePreKeys: { id: number; publicKey: string }[] = []
  for (let i = 1; i <= 10; i++) {
    const keyPair = nacl.box.keyPair()
    localStorage.setItem(`lynkstar_otpk_${i}`, JSON.stringify({
      keyId: i,
      publicKey: encodeBase64(keyPair.publicKey),
      secretKey: encodeBase64(keyPair.secretKey),
    }))
    oneTimePreKeys.push({
      id: i,
      publicKey: encodeBase64(keyPair.publicKey),
    })
  }

  return {
    identityKey: encodeBase64(identityKeyPair.publicKey),
    signedPreKey: {
      id: 1,
      publicKey: encodeBase64(signedPreKeyPair.publicKey),
      signature: encodeBase64(signedPreKeySignature),
    },
    oneTimePreKeys,
  }
}
